import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Col, DatePicker,
  message,
  Modal, Row,
  Typography
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import * as yup from 'yup';

import Input from '../../common/components/Input';

import { copyTimeKeeping, createPayrollPeriodsService, updatePayrollPeriodsByIdService } from 'common/services/extends/payrollPeriods';
import { CopyTimeKeepingParamsTypes, CreatePayrollPeriodsParams, UpdatePayrollPeriodsParams } from 'common/services/extends/payrollPeriods/types';
import { detectErrorByCode } from 'common/utils/functions';

export interface TableForm {
  name: string;
  rangeDate?: [string, string];
}

interface TimeKeepingTableModalProps {
  isOpen: {
    id?: number,
    isOpen: boolean,
    type?: 'create' | 'update' | 'copy',
  };
  handleClose: () => void;
  setModalWarningEditTimeKeeping: (data: {
    isOpen: boolean;
    err?: {
      dates?: string[];
    };
  }) => void;
  projectDuration: Dayjs[];
  dataDefault?: TableForm;
  queryKey?: string | string[];
  nameTimeKeeping?: string[];
}

const defaultValues: TableForm = {
  name: '',
  rangeDate: undefined,
};

const TimeKeepingTableModal: React.FC<TimeKeepingTableModalProps> = ({
  isOpen,
  handleClose,
  setModalWarningEditTimeKeeping,
  projectDuration,
  dataDefault,
  queryKey,
  nameTimeKeeping
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('projectId') || '';
  const staffFullTimeId = searchParams.get('staffFullTimeId') || '';

  const timeKeepingSchema: yup.SchemaOf<{
    name: string;
    rangeDate: [string, string];
  }> = yup.object({
    name: yup.string().required('Thông tin bắt buộc'),
    rangeDate: yup
      .array()
      .of(yup.string().required('Thông tin bắt buộc'))
      .length(2, 'Phải chọn đủ ngày bắt đầu và kết thúc')
      .required('Thông tin bắt buộc')
      .nullable()
      .test(
        'just-a-month',
        'Kỳ chấm công chỉ được tạo trong cùng 1 tháng',
        (value): boolean => {
          if (!value || value.length !== 2) return false;
          const [startRaw, endRaw] = value;
          const start = dayjs(startRaw);
          const end = dayjs(endRaw);

          // Nếu người dùng cố tình chọn ngày khác tháng => báo lỗi
          if (start && end && start.month() !== end.month()) {
            return false;
          }
          return true;
        }
      ) as yup.SchemaOf<[string, string]>,
  });

  const method = useForm<TableForm>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(timeKeepingSchema),
  });

  const { mutate: createPayroll, isLoading: createLoading } = useMutation(
    ['create-payroll-periods'],
    async (params: CreatePayrollPeriodsParams) => createPayrollPeriodsService(params),
    {
      onSuccess: () => {
        message.success(t('message.createSuccess'));
        queryClient.invalidateQueries(queryKey);
        method.reset(defaultValues);
        handleClose();
      },
      onError: (errors: any) => {
        const errs = Array.isArray(errors) ? errors : [];
        if (errs.length > 0) {
          errs.forEach((err) => {
            method.setError(err.field, {
              type: 'manual',
              message: t(detectErrorByCode(err.code, err.field, err.message)),
            });
            message.error(
              t(detectErrorByCode(err.code, err.field, err.message))
            );
          });
        } else {
          message.error(t('message.createError'));
        }
      },
    }
  );

  const handleCreate = (dataForm: TableForm) => {
    const submitData = {
      projectId: Number(projectId),
      staffFullTimeId: Number(staffFullTimeId),
      startDate: dataForm.rangeDate ? dayjs(dataForm.rangeDate[0]).format('YYYY-MM-DD') : '',
      endDate: dataForm.rangeDate ? dayjs(dataForm.rangeDate[1]).format('YYYY-MM-DD') : '',
      name: dataForm.name,
    };
    createPayroll(submitData);
  };

  const { mutate: updatePayroll, isLoading: updateLoading } = useMutation(
    ['update-payroll-periods'],
    async (params: {
      id: string,
      params: UpdatePayrollPeriodsParams
    }) => updatePayrollPeriodsByIdService(params.id, params.params),
    {
      onSuccess: () => {
        message.success(t('message.createSuccess'));
        queryClient.invalidateQueries(queryKey);
        method.reset(defaultValues);
        handleClose();
      },
      onError: (errors: ErrorResponse[]) => {
        const errs = Array.isArray(errors) ? errors : [];
        if (errs.length > 0) {
          errs.forEach((err) => {
            if (err.code === 'hasAttendanceRecord') {
              setModalWarningEditTimeKeeping({
                isOpen: true,
                err: {
                  dates: err.dates || []
                }
              });
            } else {
              message.error(
                t(detectErrorByCode(err.code, err.field, err.message))
              );
            }
          });
        } else {
          message.error(t('message.updateError'));
        }
      }
    }
  );

  const { mutate: copyTimeKeepingMuate, isLoading: copyTimeKeepingLoading } = useMutation(
    ['create-payroll-periods'],
    async (params: CopyTimeKeepingParamsTypes) => copyTimeKeeping(params),
    {
      onSuccess: () => {
        message.success(t('message.copyTimeKeepingSuccess'));
        queryClient.invalidateQueries(queryKey);
        method.reset(defaultValues);
        handleClose();
      },
      onError: (errors: any) => {
        const errs = Array.isArray(errors) ? errors : [];
        if (errs.length > 0) {
          errs.forEach((err) => {
            method.setError(err.field, {
              type: 'manual',
              message: t(detectErrorByCode(err.code, err.field, err.message)),
            });
            message.error(
              t(detectErrorByCode(err.code, err.field, err.message))
            );
          });
        } else {
          message.error(t('message.copyTimeKeepingError'));
        }
      },
    }
  );

  const handleUpdate = (dataForm: TableForm) => {
    const submitData = {
      projectId: Number(projectId),
      staffFullTimeId: Number(staffFullTimeId),
      startDate: dataForm.rangeDate ? dayjs(dataForm.rangeDate[0]).format('YYYY-MM-DD') : '',
      endDate: dataForm.rangeDate ? dayjs(dataForm.rangeDate[1]).format('YYYY-MM-DD') : '',
      name: dataForm.name,
    };
    if (isOpen.id) {
      updatePayroll({
        id: isOpen.id.toString(),
        params: submitData
      });
    }
  };

  const handleCopyTimeKeeping = (dataForm: TableForm) => {
    if (!isOpen.id) return;
    copyTimeKeepingMuate({
      idPayroll: isOpen.id,
      name: dataForm.name,
      startDate: dataForm.rangeDate ? dayjs(dataForm.rangeDate[0]).format('YYYY-MM-DD') : '',
      endDate: dataForm.rangeDate ? dayjs(dataForm.rangeDate[1]).format('YYYY-MM-DD') : '',
    });
  };

  const title = useMemo(() => {
    if (isOpen.type === 'create') return t('timesheet.addPeriod');
    if (isOpen.type === 'copy') return t('timesheet.copyPeriod');
    return t('timesheet.editPeriod');
  }, [isOpen.type]);

  const handleSubmit = async () => {
    const isValid = await method.trigger();
    if (!isValid) return;

    const formData = method.getValues();

    const name = formData.name.trim();

    if (name && nameTimeKeeping?.includes(name) && isOpen.type !== 'update') {
      method.setError('name', {
        type: 'manual',
        message: t('timesheet.nameAlreadyExists')
      });
      return;
    }

    if (isOpen.type === 'copy' && handleCopyTimeKeeping) {
      handleCopyTimeKeeping(formData);
    } else if (isOpen.type === 'update' && handleUpdate) {
      handleUpdate(formData);
    } else {
      handleCreate(formData);
    }
  };

  useEffect(() => {
    if (dataDefault && isOpen.type === 'update') {
      method.reset({
        name: dataDefault.name,
        rangeDate: dataDefault.rangeDate
      });
    } else if (dataDefault && isOpen.type === 'copy') {
      const nameTK = `${dataDefault.name} - copy`;
      method.reset({
        name: nameTK,
        rangeDate: dataDefault.rangeDate
      });

      // check if nameTimeKeeping already exists
      if (nameTK && nameTimeKeeping?.includes(nameTK)) {
        method.setError('name', {
          type: 'manual',
          message: t('timesheet.nameAlreadyExists')
        });
      } else {
        method.clearErrors('name');
      }
    }
  }, [dataDefault, isOpen.type, nameTimeKeeping]);

  return (
    <Modal
      title={(
        <Typography.Title
          level={4}
        >
          {title}
        </Typography.Title>
      )}
      open={isOpen.isOpen}
      footer={null}
      closable={false}
      centered
      width={800}
      onCancel={handleClose}
    >
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{ width: '100%' }}>
          <Row gutter={16}>
            <Col span={12} className="u-mt-8">
              <Typography.Text strong>
                {t('system.name')}
                {' '}
              </Typography.Text>
              <Typography.Text strong type="danger">
                *
              </Typography.Text>
              <Controller
                name="name"
                control={method.control}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <Input
                    className="u-mt-8"
                    value={value}
                    onChange={onChange}
                    error={error?.message}
                    size="large"
                  />
                )}
              />
            </Col>
            <Col span={12} className="u-mt-8">
              <Typography.Text strong>
                {`${t('timesheet.fromDate')} - ${t('timesheet.endDate')}`}
                {' '}
              </Typography.Text>
              <Typography.Text strong type="danger">
                *
              </Typography.Text>
              <div className="u-mt-8">
                <Controller
                  name="rangeDate"
                  control={method.control}
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <>
                      <DatePicker.RangePicker
                        value={value ? [dayjs(value[0], 'YYYY-MM-DD'), dayjs(value[1], 'YYYY-MM-DD')] : undefined}
                        onChange={onChange}
                        size="large"
                        format="DD-MM-YYYY"
                        style={{ width: '100%' }}
                        disabledDate={
                          (date) => date.isBefore(projectDuration[0])
                            || date.isAfter(projectDuration[1])
                        }
                      />
                      {error && (
                        <span
                          className="a-input_errorMessage"
                        >
                          {error?.message}
                        </span>
                      )}
                    </>
                  )}
                />
              </div>
            </Col>
          </Row>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            width: '100%',
            marginTop: 20
          }}
        >
          <Button
            size="middle"
            type="primary"
            disabled={copyTimeKeepingLoading || createLoading || updateLoading}
            onClick={() => handleSubmit()}
            style={{ marginRight: '10px' }}
          >
            {t('system.save')}
          </Button>
          <Button
            size="middle"
            type="default"
            disabled={copyTimeKeepingLoading || createLoading || updateLoading}
            onClick={() => {
              handleClose();
              method.reset(defaultValues);
            }}
          >
            {t('system.cancel')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default TimeKeepingTableModal;
